/**
 * 借贷区（L2）的**分组与卡片数据** —— 纯函数，无 React、无 IO（可单测）
 *
 * 依据：`Q域PRD` `AC-Q4.2`（按 `kind` 分组）· `AC-Q4.4`（分组 + 折叠）·
 * `AC-Q4.5`（卡片四要素）· `AC-Q4.6`（色彩守铁律 8）·
 * 原型 `docs/03-UX与UI设计/15-债权债务_原型.html:290-320`（卡片解剖）·
 * `借贷域口径对齐单` line 445（「应收为正=红、应付为负=负债语义色」）。
 *
 * ## 🔴 本文件是"卡片长什么样"的**唯一实现处**
 *
 * 页面只负责画，**不判**"哪类显示负数""副行拼什么""什么时候留白"——
 * 这些判据一旦散到 JSX 里，下一次改口径就会漏改一半（`DR-021` §6.1 唯一实现）。
 *
 * ## 🔴 两条最容易做错的规则（都来自实测/原文，不是审美选择）
 *
 * ### ① 负债侧（`payable` / `loan`）的余额**取反**后展示
 *
 * 库里 `contra` 余额的实态是「**正数 = 还欠**」（批 `80` D4 实测：dev 库三笔贷款全为正）。
 * 若直接把它当正数渲染 ⇒ 金额变**红**（红=正）⇒ **红色在表示"这是一笔负债"**，
 * 正是 `AC-Q4.6` 点名禁止的那件事（铁律 8）。
 * 原型也是这么画的：借入卡显示 `¥−30,000.00`（`15-债权债务_原型.html:311`）。
 * ⇒ **只翻转展示符号，不改库里任何一个数**；资产侧（应收 / 垫付 / 报销）原样。
 *
 * ### ② 到期日**空则整块留白**，不显示"无期限"
 *
 * `AC-Q4.5` 原文：「到期日（**空则留白，不显示"无期限"占位**）」⇒ 返回 `null`，
 * 由页面决定"不渲染这个 span"，而不是渲染一个 `—` 或 `无期限`。
 *
 * ## ⚠️ 诚实登记（本批不做）
 *
 * - **只给"期数进度"**：`已还 N / M 期` 需要 `terms` + `loan_schedules` 计数；
 *   应收 / 应付 / 垫付 / 报销**没有分母可算**（原始总额在 v2 无载体，见 `LoanProgressView` 注释）
 *   ⇒ 它们的 `progressText` 恒为 `null`（留白），不编造"已收 30%"。
 * - **类型标签用中性 outlined chip**（不按 `kind` 上语义色）：原型给应付用了
 *   `tag-alert`（朱红），与铁律 8「红色不表示负债状态」相冲 ⇒ 本批取中性，
 *   原型该处待订正（已登记 changelog，不擅自改设计稿）。
 */

import {
  AccountType,
  ContraKind,
  type AccountView,
  type LoanProgressView,
} from '@jiaying/shared';
import { contraKindShortLabel } from './account-labels.js';

/**
 * 分组顺序 —— **不是随机的**：资产侧三类 → 负债侧两类，与「钱在别人那 → 我欠别人」
 * 的阅读顺序一致（原型同序：应收 → 应付 → 垫付 → 报销）。
 *
 * 🔴 用常量而不是 `Object.values(ContraKind)`：枚举顺序是实现细节，
 * 改一次枚举顺序不该让页面重组（顺序是**产品决定**）。
 */
export const CONTRA_GROUP_ORDER: readonly ContraKind[] = [
  ContraKind.Receivable,
  ContraKind.Payable,
  ContraKind.Advance,
  ContraKind.Reimburse,
  ContraKind.Loan,
];

/** 负债侧（余额按"欠多少"存在 ⇒ 展示时取反）：应付 / 贷款 */
export function isLiabilitySide(kind: ContraKind): boolean {
  return kind === ContraKind.Payable || kind === ContraKind.Loan;
}

/**
 * 展示用余额（整数分）：负债侧**取反**，资产侧原样。
 *
 * 🔴 只是符号 —— 绝对值与库里一致，**不做任何换算**（`DR-004`：原币、不折算）。
 */
export function displayBalanceCents(kind: ContraKind, balanceCents: number): number {
  return isLiabilitySide(kind) ? -balanceCents : balanceCents;
}

/** 卡片模型（页面只读它，不再自己拼文案） */
export interface ContraCardModel {
  id: string;
  /** 主行：账户名（`DR-022` §2.1：往来款项的 `name` 承载**事项**） */
  name: string;
  kind: ContraKind;
  /** 类型中文（中性 chip，不用语义色） */
  kindLabel: string;
  currency: string;
  /** 右侧余额（**已按侧取号**：负债侧为负 ⇒ 端侧 `Amount` 自然渲染成"负"色 = 铁律 8） */
  balanceCents: number;
  /**
   * 副行：「对手方 · 归属人」。
   *
   * 🔴 **去重规则**：`counterparty === name` 时不重复显示 ——
   * 迁移行上二者必然相同（v1 把"事项"与"对手方"融在 `contra.counterparty` 一列，
   * 批 `78` 把它写进了 `accounts.name`），原样拼会渲染出「张瑞 · 沈琰滟」而主行也是「张瑞」。
   * 这不是数据错误，是源数据不可拆分（`DR-046` §5 登记 1/2）。
   */
  subtitle: string;
  /** 「已还 2 / 6 期」；`null` = **不显示**（无总期数 / 无计划表 / 非贷款） */
  progressText: string | null;
  /** 「到期 2027-03」；`null` = **留白**（`AC-Q4.5`：不显示"无期限"占位） */
  dueText: string | null;
}

/** 一组（一个 `kind`） */
export interface ContraGroupModel {
  kind: ContraKind;
  /** 组标题（含数量由页面拼，避免本函数产出"给人看的排版细节"） */
  label: string;
  cards: readonly ContraCardModel[];
}

/**
 * 副行文案：「对手方 · 归属人」（去重同名 + 去空）。
 *
 * 归属人**恒显示**（`ownerId` NOT NULL，`DR-018`）—— 一个借贷账户必然属于某个人，
 * 不显示反而会让"这笔钱是谁的"变成隐藏信息。
 */
export function contraSubtitle(account: AccountView): string {
  const cp = account.counterparty?.trim() ?? '';
  const parts: string[] = [];
  if (cp !== '' && cp !== account.name.trim()) parts.push(cp);
  parts.push(account.ownerName);
  return parts.join(' · ');
}

/**
 * 进度文案（`AC-Q4.5` 的「进度」）。
 *
 * 判据三条，缺一不出：
 * 1. 该组 `kind` 必须是 `loan`（只有贷款有"期"）；
 * 2. 账户上要有**总期数**（`contraTerms.terms`，`DR-044` 六列之一）；
 * 3. 服务端要有**计划表计数**（`LoanProgressView`）—— 没有计划表 ⇒ 不知道还了几期。
 *
 * 🔴 **不用 `pendingPeriods` 倒算已还**：`terms` 是用户填的、`pending` 是计划表算的，
 * 二者在退化态下会不一致（参数不全时根本没有计划表）⇒ 用服务端给的 `paidPeriods` 原值。
 */
export function loanProgressText(account: AccountView, progress: LoanProgressView | undefined): string | null {
  if (account.contraKind !== ContraKind.Loan) return null;
  const terms = account.contraTerms?.terms ?? null;
  if (terms === null || progress === undefined) return null;
  return `已还 ${String(progress.paidPeriods)} / ${String(terms)} 期`;
}

/**
 * 到期文案（`AC-Q4.5`：「到期日（**空则留白**）」）。
 *
 * 🔴 只取 **年月**（`2027-03`）：卡面是扫读用的，日精度是噪音；
 * 原型同样是 `到期 2027-03`（`15-债权债务_原型.html:309`）。
 * ⚠️ 这里**不是**日期运算，只是截字符串 —— 故不违反"日期一律走内核"的纪律。
 */
export function contraDueText(account: AccountView): string | null {
  const d = account.contraTerms?.dueDate ?? null;
  if (d === null || d.length < 7) return null;
  return `到期 ${d.slice(0, 7)}`;
}

/** 单个账户 → 卡片模型 */
export function contraCardOf(account: AccountView, progress: LoanProgressView | undefined): ContraCardModel {
  const kind = account.contraKind as ContraKind;
  return {
    id: account.id,
    name: account.name,
    kind,
    kindLabel: contraKindShortLabel(kind),
    currency: account.currency,
    balanceCents: displayBalanceCents(kind, account.balance),
    subtitle: contraSubtitle(account),
    progressText: loanProgressText(account, progress),
    dueText: contraDueText(account),
  };
}

/**
 * 全部 `contra` 账户 → **按 `kind` 分组**的卡片模型（`AC-Q4.2`）。
 *
 * - 只收 `type='contra'` 且 `contraKind` 非空的账户（`type='loan'` 的独立账户**不在本区** ——
 *   实测 v2 全库 0 个，且它没有 `contraKind`；若将来出现需另议，见 changelog 登记）；
 * - **空组不产出**（`AC-Q4.4` 的"用分组化解膨胀"，不是"用一排空标题"）；
 * - 组内保持服务端顺序（`sort → createdAt → id`，服务端已稳定序）。
 */
export function buildContraGroups(
  accounts: readonly AccountView[],
  progress: readonly LoanProgressView[],
): ContraGroupModel[] {
  const progressById = new Map(progress.map((p) => [p.accountId, p]));
  const byKind = new Map<ContraKind, ContraCardModel[]>();
  for (const a of accounts) {
    if (a.type !== AccountType.Contra) continue;
    if (a.contraKind === null) continue;
    const kind = a.contraKind;
    const list = byKind.get(kind) ?? [];
    list.push(contraCardOf(a, progressById.get(a.id)));
    byKind.set(kind, list);
  }
  return CONTRA_GROUP_ORDER.filter((k) => byKind.has(k)).map((k) => ({
    kind: k,
    label: contraKindShortLabel(k),
    cards: byKind.get(k) ?? [],
  }));
}

/** `contra` 账户总数（页面决定"要不要渲染整个借贷区"用） */
export function countContraAccounts(accounts: readonly AccountView[]): number {
  return accounts.filter((a) => a.type === AccountType.Contra).length;
}
